import { spawnSync } from 'node:child_process';
import { forComponent, type Hooks } from '../src/index';

/**
 * Config-driven shell hooks for the CLI. Turns a declarative `hooks` block in
 * `.agent/config.*` into the runtime's `Hooks` seam — so a user can gate or audit
 * tool calls without embedding TypeScript (the Claude-Code `settings.json` model).
 * Node-only by design (runs shell commands): the pure hook engine stays in `src/`.
 *
 * Each matched rule runs `command` via the shell with the event payload in env:
 *   AGENTLIBX_EVENT, AGENTLIBX_TOOL, AGENTLIBX_ARGS (JSON), AGENTLIBX_RESULT, AGENTLIBX_TEXT.
 * For preToolUse with `block: true`, a non-zero exit BLOCKS the call (stdout/stderr → reason).
 */
const log = forComponent('hooks');

export interface HookRule {
  /** Match the tool name. `*` wildcards (e.g. "Web*"); omit / "*" matches any tool. */
  tool?: string;
  /** Shell command to run. */
  command: string;
  /** preToolUse only: block the tool call if the command exits non-zero. */
  block?: boolean;
  /** Per-command timeout (ms, default 10000). */
  timeoutMs?: number;
}
export interface HooksConfig {
  preToolUse?: HookRule[];
  postToolUse?: HookRule[];
  onStop?: HookRule[];
}

const escapeRegex = (s: string) => s.replace(/[.+^${}()|[\]\\]/g, '\\$&');

/** Does a rule apply to a tool name? Supports `*` wildcards; missing/"*" tool matches all. */
export function ruleMatches(rule: HookRule, toolName: string): boolean {
  if (!rule.tool || rule.tool === '*') return true;
  const re = new RegExp('^' + rule.tool.split('*').map(escapeRegex).join('.*') + '$');
  return re.test(toolName);
}

function runCmd(rule: HookRule, env: Record<string, string>): { code: number; out: string } {
  try {
    const r = spawnSync(rule.command, {
      shell: true,
      encoding: 'utf8',
      timeout: rule.timeoutMs ?? 10_000,
      env: { ...process.env, ...env },
    });
    return { code: r.status ?? 1, out: ((r.stdout ?? '') + (r.stderr ?? '')).trim() };
  } catch (e) {
    log.debug(`hook command failed: ${rule.command}`, e);
    return { code: 1, out: String((e as any)?.message ?? e) };
  }
}

const CAP = 8192; // bound payload env vars

/** Build a runtime `Hooks` object from a declarative config block. */
export function hooksFromConfig(cfg: HooksConfig): Hooks {
  return {
    async preToolUse(call) {
      for (const rule of cfg.preToolUse ?? []) {
        if (!ruleMatches(rule, call.name)) continue;
        const { code, out } = runCmd(rule, {
          AGENTLIBX_EVENT: 'preToolUse',
          AGENTLIBX_TOOL: call.name,
          AGENTLIBX_ARGS: JSON.stringify(call.args ?? {}).slice(0, CAP),
        });
        if (rule.block && code !== 0) return { block: true, reason: out || `blocked by hook (exit ${code})` };
      }
    },
    async postToolUse(call, result) {
      for (const rule of cfg.postToolUse ?? []) {
        if (!ruleMatches(rule, call.name)) continue;
        runCmd(rule, {
          AGENTLIBX_EVENT: 'postToolUse',
          AGENTLIBX_TOOL: call.name,
          AGENTLIBX_ARGS: JSON.stringify(call.args ?? {}).slice(0, CAP),
          AGENTLIBX_RESULT: String(result).slice(0, CAP),
        });
      }
    },
    onStop(text) {
      for (const rule of cfg.onStop ?? []) {
        runCmd(rule, { AGENTLIBX_EVENT: 'onStop', AGENTLIBX_TEXT: String(text ?? '').slice(0, CAP) });
      }
    },
  };
}
